import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import httpRequest from '../utils/request';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useModalEscape } from '../utils/useModalEscape';
import {
  FaArrowLeft,
  FaPlus,
  FaMinus,
  FaTrash,
  FaRightLeft,
  FaFileInvoiceDollar,
  FaKitchenSet,
  FaCashRegister,
  FaMagnifyingGlass,
  FaUser,
  FaUsers,
  FaChair,
  FaRegNoteSticky,
  FaXmark,
  FaCheck,
  FaCheckDouble,
  FaPrint
} from 'react-icons/fa6';

export default function RestaurantPOS() {
  const { tableId } = useParams();
  const { user: _user } = useSelector((s) => s.auth);
  const navigate = useNavigate();

  // Core POS State
  const [table, setTable] = useState(null);
  const [order, setOrder] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [freeTables, setFreeTables] = useState([]);

  // Filter & Search Catalog State
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todos');

  // Active Draft Items State
  // We keep a local state of items for snappy interactions
  const [localItems, setLocalItems] = useState([]);
  const [dinersCount, setDinersCount] = useState(1);
  const [orderNotes, setOrderNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Modals state
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTargetTable, setSelectedTargetTable] = useState('');
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteItemIndex, setNoteItemIndex] = useState(null);
  const [tempNoteText, setTempNoteText] = useState('');
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [variantProduct, setVariantProduct] = useState(null);

  // Checkout Form State
  const [checkoutCustomer, setCheckoutCustomer] = useState('');
  const [checkoutDiscount, setCheckoutDiscount] = useState(0);
  const [paymentType, setPaymentType] = useState('Efectivo'); // Efectivo | Tarjeta | Transferencia | Cuenta corriente | Múltiple
  const [cashReceived, setCashReceived] = useState('');
  
  // Mixed Payments list
  const [payments, setPayments] = useState([]);
  const [tempPaymentMethod, setTempPaymentMethod] = useState('Efectivo');
  const [tempPaymentAmount, setTempPaymentAmount] = useState('');

  // Post-checkout print ticket state
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [printUrl, setPrintUrl] = useState('');
  const [successSale, setSuccessSale] = useState(null);

  // ESC Key listener setups
  useModalEscape(showTransferModal, () => setShowTransferModal(false));
  useModalEscape(showCheckoutModal, () => {
    if (!showSuccessScreen) setShowCheckoutModal(false);
  });
  useModalEscape(showNoteModal, () => setShowNoteModal(false));
  useModalEscape(showVariantModal, () => setShowVariantModal(false));

  // Load Initial Data
  const loadInitialData = async () => {
    try {
      // 1. Fetch tables to find the current active one and populate order
      const resTables = await httpRequest.get('/restaurant/tables');
      const foundTable = resTables.data.find(t => t._id === tableId);
      if (!foundTable) {
        toast.error('❌ Mesa no encontrada');
        navigate('/salon');
        return;
      }
      setTable(foundTable);

      const activeOrder = foundTable.activeOrderId;
      if (!activeOrder) {
        toast.error('❌ La mesa no está abierta');
        navigate('/salon');
        return;
      }
      setOrder(activeOrder);
      setLocalItems(activeOrder.items || []);
      setDinersCount(activeOrder.dinersCount || 1);
      setOrderNotes(activeOrder.notes || '');

      // Get list of free tables for transfer modal
      setFreeTables(resTables.data.filter(t => t.status === 'free'));

      // 2. Fetch products
      const resProducts = await httpRequest.get('/product/all-products');
      const prodList = Array.isArray(resProducts.data) ? resProducts.data : [];
      setProducts(prodList);

      // Extract unique categories from active products
      const extractedCats = ['Todos', ...new Set(prodList.map(p => p.category).filter(Boolean))];
      setCategories(extractedCats);

      // 3. Fetch customers for checkout options
      const resCust = await httpRequest.get('/customers');
      setCustomers(Array.isArray(resCust.data) ? resCust.data : (resCust.data?.items || []));

    } catch (err) {
      console.error(err);
      toast.error('❌ Error al inicializar el POS');
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [tableId]);

  // Derived Totals
  const localSubtotal = useMemo(() => {
    return localItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [localItems]);

  const checkoutTotal = useMemo(() => {
    const disc = Number(checkoutDiscount) || 0;
    return Math.max(0, localSubtotal - disc);
  }, [localSubtotal, checkoutDiscount]);

  // Cash change calculator
  const cashChange = useMemo(() => {
    const received = Number(cashReceived) || 0;
    return Math.max(0, received - checkoutTotal);
  }, [cashReceived, checkoutTotal]);

  // Product Selection Handlers
  const handleProductClick = (product) => {
    // Check if product has variants
    if (product.variants && product.variants.length > 0) {
      setVariantProduct(product);
      setShowVariantModal(true);
      return;
    }

    addProductToOrder(product);
  };

  const addProductToOrder = (product, selectedVariant = null) => {
    setLocalItems(prev => {
      const pId = product._id || product.id;
      const vId = selectedVariant ? (selectedVariant.sku || selectedVariant._id) : null;
      const vName = selectedVariant ? 
        Object.entries(selectedVariant.attributes || {}).map(([k, v]) => `${k}: ${v}`).join(', ') : 
        null;
      const price = selectedVariant ? selectedVariant.price : product.salePrice;

      // Check if variant or simple product already in local order (draft or sent)
      const existingIndex = prev.findIndex(item => 
        item.productId.toString() === pId.toString() &&
        item.variantId === vId
      );

      if (existingIndex > -1) {
        const updated = [...prev];
        const existingItem = updated[existingIndex];
        updated[existingIndex] = {
          ...existingItem,
          quantity: existingItem.quantity + 1,
          sentToKitchen: false // Reset to draft status as quantity has increased
        };
        return updated;
      } else {
        return [...prev, {
          productId: pId,
          name: product.name,
          quantity: 1,
          price: Number(price || 0),
          variantId: vId,
          variantName: vName,
          sentToKitchen: false,
          notes: ''
        }];
      }
    });
    toast.info(`➕ ${product.name} agregado`);
  };

  // Quantity controllers on order detail (Left Panel)
  const adjustQuantity = (index, delta) => {
    setLocalItems(prev => {
      const updated = [...prev];
      const item = updated[index];
      const newQty = item.quantity + delta;

      if (newQty <= 0) {
        // If sent to kitchen, don't allow deletion without confirmation
        if (item.sentToKitchen) {
          if (!window.confirm('Este producto ya fue enviado a cocina. ¿Deseas eliminarlo del pedido?')) {
            return prev;
          }
        }
        updated.splice(index, 1);
        return updated;
      }

      updated[index] = { 
        ...item, 
        quantity: newQty,
        sentToKitchen: delta > 0 ? false : item.sentToKitchen // Reset to false only if quantity increased
      };
      return updated;
    });
  };

  // Trigger Note modal for item
  const openNoteForItem = (index) => {
    setNoteItemIndex(index);
    setTempNoteText(localItems[index].notes || '');
    setShowNoteModal(true);
  };

  const saveItemNote = () => {
    if (noteItemIndex === null) return;
    setLocalItems(prev => {
      const updated = [...prev];
      updated[noteItemIndex].notes = tempNoteText;
      return updated;
    });
    setShowNoteModal(false);
    setNoteItemIndex(null);
  };

  // Persist current localItems drafts to database
  const saveOrderItems = async (showToast = true) => {
    if (!order) return false;
    try {
      setIsSaving(true);
      const { data } = await httpRequest.put(`/restaurant/orders/${order._id}/items`, {
        items: localItems,
        dinersCount: Number(dinersCount),
        notes: orderNotes
      });
      setOrder(data);
      setLocalItems(data.items || []);
      if (showToast) toast.success('💾 Pedido guardado en mesa');
      return true;
    } catch (err) {
      console.error(err);
      toast.error('❌ Error al guardar modificaciones');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Quick Action: Send Comanda to Kitchen
  const handleSendToKitchen = async () => {
    if (localItems.length === 0) {
      toast.warn('No hay productos cargados en la mesa');
      return;
    }

    // 1. Save items first to sync draft additions
    const saved = await saveOrderItems(false);
    if (!saved) return;

    // 2. Trigger send to kitchen API call
    try {
      await httpRequest.post(`/restaurant/orders/${order._id}/kitchen`);
      toast.success('🍳 ¡Comanda enviada a cocina con éxito!');
      
      // Reload table order details to mark items as sentToKitchen: true
      loadInitialData();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al enviar a cocina';
      toast.error(`❌ ${msg}`);
    }
  };

  // Quick Action: Request Pre-bill
  const handleRequestPreBill = async () => {
    // 1. Save items first to ensure total is up to date
    const saved = await saveOrderItems(false);
    if (!saved) return;

    try {
      await httpRequest.post(`/restaurant/orders/${order._id}/pre-bill`);
      toast.success('🧾 Pre-cuenta solicitada. Mesa en espera de pago.');
      loadInitialData();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al pedir pre-cuenta';
      toast.error(`❌ ${msg}`);
    }
  };

  // Quick Action: Transfer Table
  const handleTransferTable = async () => {
    if (!selectedTargetTable) {
      toast.warn('Selecciona una mesa destino');
      return;
    }

    try {
      await httpRequest.post('/restaurant/orders/transfer', {
        sourceTableId: table._id,
        targetTableId: selectedTargetTable
      });
      toast.success('🔀 Consumos mudados exitosamente');
      setShowTransferModal(false);
      // Navigate directly to the new table!
      navigate(`/restaurant-pos/${selectedTargetTable}`);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al mudar mesa';
      toast.error(`❌ ${msg}`);
    }
  };

  // Quick Action: Open Checkout Modal
  const handleOpenCheckout = async () => {
    if (localItems.length === 0) {
      toast.warn('No se puede cobrar una mesa sin consumos');
      return;
    }
    // Sync current changes before checking out
    const saved = await saveOrderItems(false);
    if (!saved) return;

    setCheckoutDiscount(0);
    setCheckoutCustomer('');
    setPaymentType('Efectivo');
    setCashReceived('');
    setPayments([]);
    setShowCheckoutModal(true);
  };

  // Mixed payments helper handlers
  const handleAddPayment = () => {
    const amt = Number(tempPaymentAmount) || 0;
    if (amt <= 0) {
      toast.warn('Ingresa un monto válido mayor a 0');
      return;
    }

    setPayments(prev => [...prev, { method: tempPaymentMethod, amount: amt }]);
    setTempPaymentAmount('');
    toast.success(`➕ Agregado: $${amt} en ${tempPaymentMethod}`);
  };

  const handleRemovePayment = (index) => {
    setPayments(prev => prev.filter((_, i) => i !== index));
  };

  const accumulatedMixedPaid = useMemo(() => {
    return payments.reduce((sum, p) => sum + Number(p.amount), 0);
  }, [payments]);

  // Submit checkout closings
  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (paymentType === 'Múltiple' && Math.abs(accumulatedMixedPaid - checkoutTotal) > 0.05) {
      toast.error(`❌ Los pagos acumulados ($${accumulatedMixedPaid}) deben coincidir exactamente con el total ($${checkoutTotal})`);
      return;
    }

    const payload = {
      paymentMethod: paymentType,
      payments: paymentType === 'Múltiple' ? payments : [{ method: paymentType, amount: checkoutTotal }],
      amountPaid: paymentType === 'Efectivo' ? (Number(cashReceived) || checkoutTotal) : checkoutTotal,
      change: paymentType === 'Efectivo' ? cashChange : 0,
      saleDiscount: Number(checkoutDiscount) || 0,
      customer: checkoutCustomer || null,
      customerName: checkoutCustomer ? (customers.find(c => c._id === checkoutCustomer)?.nombre || 'Consumidor Final') : 'Consumidor Final'
    };

    try {
      const { data } = await httpRequest.post(`/restaurant/orders/${order._id}/checkout`, payload);
      toast.success('💸 ¡Mesa cobrada y liberada con éxito!');
      
      // Set print URLs and success state instead of instant redirect
      if (data.printUrl) {
        setPrintUrl(data.printUrl);
      }
      setSuccessSale(data.sale || payload);
      setShowSuccessScreen(true);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Error al procesar el cobro';
      toast.error(`❌ ${msg}`);
    }
  };

  // Final exit from successful checkout screen
  const finishPOSSession = () => {
    setShowSuccessScreen(false);
    setShowCheckoutModal(false);
    navigate('/salon');
  };

  // Product Catalog Filtering
  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            (p.barcode && p.barcode.includes(searchQuery));
      const matchesCategory = activeCategory === 'Todos' || p.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchQuery, activeCategory]);

  return (
    <div className="pos-container">
      {/* Injected Premium POS styling */}
      <style>{`
        .pos-container {
          display: flex;
          flex-direction: column;
          height: 100vh;
          font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          background: #f8fafc;
          color: #1e293b;
          overflow: hidden;
        }

        .pos-header {
          background: white;
          border-bottom: 1px solid #e2e8f0;
          padding: 12px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .btn-back {
          width: 40px;
          height: 40px;
          border-radius: 10px;
          border: 1px solid #e2e8f0;
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #475569;
          transition: all 0.2s;
        }

        .btn-back:hover {
          background: #f1f5f9;
          color: #0f172a;
        }

        .table-details {
          display: flex;
          flex-direction: column;
        }

        .table-title {
          font-size: 18px;
          font-weight: 800;
          color: #0f172a;
          margin: 0;
        }

        .table-meta {
          font-size: 12px;
          color: #64748b;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 2px;
        }

        .badge-status {
          padding: 3px 8px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
        }
        .badge-status.occupied { background: #fee2e2; color: #ef4444; }
        .badge-status.waiting_payment { background: #fffbeb; color: #d97706; }

        .pos-main {
          display: flex;
          flex: 1;
          overflow: hidden;
        }

        /* LEFT PANEL: Order consumption details */
        .order-panel {
          width: 440px;
          background: white;
          border-right: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
          height: 100%;
          flex-shrink: 0;
        }

        .order-diners-bar {
          padding: 10px 16px;
          border-bottom: 1px solid #f1f5f9;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .diners-selector {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .diners-btn {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: 1px solid #cbd5e1;
          background: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #475569;
          font-size: 12px;
        }

        .diners-btn:hover { background: #f1f5f9; }

        .order-items-list {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .order-item-card {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 10px 12px;
          background: white;
          transition: all 0.2s ease;
        }

        .order-item-card.draft {
          border-left: 4px solid #3b82f6;
          background: #fdfefe;
        }

        .order-item-card.kitchen {
          border-left: 4px solid #10b981;
          background: #fcfdfc;
        }

        .item-info {
          display: flex;
          justify-content: space-between;
          font-weight: 700;
          font-size: 14px;
        }

        .item-name { color: #1e293b; }
        .item-price { color: #0f172a; }

        .item-variant {
          font-size: 11px;
          color: #3b82f6;
          font-weight: 700;
          margin-top: 2px;
        }

        .item-notes {
          font-size: 11px;
          color: #e2e8f0;
          background: #475569;
          padding: 2px 6px;
          border-radius: 4px;
          display: inline-block;
          margin-top: 4px;
          font-weight: 600;
        }

        .item-controls-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 8px;
          border-top: 1px solid #f1f5f9;
          padding-top: 6px;
        }

        .qty-controls {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .qty-btn {
          width: 24px;
          height: 24px;
          border-radius: 6px;
          border: 1px solid #cbd5e1;
          background: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 10px;
          color: #475569;
        }
        .qty-btn:hover { background: #f1f5f9; }

        .qty-value {
          font-size: 14px;
          font-weight: 800;
        }

        .item-action-btns {
          display: flex;
          gap: 8px;
        }

        .item-action-btn {
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          font-size: 13px;
          padding: 4px;
          transition: color 0.2s;
        }

        .item-action-btn:hover {
          color: #475569;
        }
        .item-action-btn.delete:hover {
          color: #ef4444;
        }

        .badge-kitchen-status {
          font-size: 11px;
          font-weight: 700;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .badge-kitchen-status.draft { color: #3b82f6; }
        .badge-kitchen-status.sent { color: #10b981; }

        .order-summary-box {
          background: #f8fafc;
          border-top: 1px solid #e2e8f0;
          padding: 16px;
          flex-shrink: 0;
        }

        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
          font-weight: 600;
          color: #64748b;
          margin-bottom: 8px;
        }

        .summary-row.total {
          font-size: 22px;
          font-weight: 900;
          color: #0f172a;
          border-top: 1px solid #cbd5e1;
          padding-top: 10px;
          margin-top: 6px;
          margin-bottom: 0;
        }

        .order-actions-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          padding: 16px;
          background: white;
          border-top: 1px solid #e2e8f0;
          flex-shrink: 0;
        }

        .btn-pos-action {
          padding: 12px;
          border-radius: 12px;
          border: none;
          font-weight: 800;
          font-size: 13px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: all 0.2s;
          color: white;
        }

        .btn-pos-action.comanda { background: linear-gradient(135deg, #f97316 0%, #ea580c 100%); }
        .btn-pos-action.comanda:hover { box-shadow: 0 4px 12px rgba(249, 115, 22, 0.35); }

        .btn-pos-action.precuenta { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
        .btn-pos-action.precuenta:hover { box-shadow: 0 4px 12px rgba(245, 158, 11, 0.35); }

        .btn-pos-action.mudar { background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%); }
        .btn-pos-action.mudar:hover { box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35); }

        .btn-pos-action.cobrar {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          grid-column: span 2;
          font-size: 16px;
          padding: 14px;
        }
        .btn-pos-action.cobrar:hover { box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35); }

        /* RIGHT PANEL: Category scroll & tactile product cards catalog */
        .catalog-panel {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          height: 100%;
          padding: 16px;
        }

        .catalog-search-bar {
          position: relative;
          margin-bottom: 12px;
          flex-shrink: 0;
        }

        .search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .input-search {
          width: 100%;
          padding: 12px 12px 12px 42px;
          border-radius: 12px;
          border: 1px solid #cbd5e1;
          background: white;
          outline: none;
          font-size: 14px;
          font-weight: 500;
          box-sizing: border-box;
          transition: all 0.2s;
        }

        .input-search:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }

        .catalog-categories {
          display: flex;
          gap: 8px;
          margin-bottom: 16px;
          overflow-x: auto;
          flex-shrink: 0;
          padding-bottom: 6px;
          scrollbar-width: none; /* Hide standard Firefox scroll */
        }
        .catalog-categories::-webkit-scrollbar { display: none; } /* Hide Chrome/Safari scroll */

        .cat-pill {
          padding: 8px 16px;
          border-radius: 9999px;
          background: white;
          border: 1px solid #e2e8f0;
          font-weight: 700;
          font-size: 13px;
          color: #64748b;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.2s;
        }

        .cat-pill.active {
          background: #0f172a;
          color: white;
          border-color: #0f172a;
        }

        .catalog-products-container {
          flex: 1;
          overflow-y: auto;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
          gap: 12px;
        }

        .product-tactile-card {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 12px;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 110px;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.02);
        }

        .product-tactile-card:hover {
          transform: translateY(-3px);
          border-color: #cbd5e1;
          box-shadow: 0 6px 12px rgba(0,0,0,0.06);
        }

        .product-tactile-card:active {
          transform: scale(0.97);
        }

        .prod-title {
          font-size: 13px;
          font-weight: 700;
          color: #1e293b;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .prod-price-badge {
          align-self: flex-start;
          background: #f1f5f9;
          padding: 3px 8px;
          border-radius: 6px;
          font-size: 12px;
          font-weight: 800;
          color: #0f172a;
          margin-top: 8px;
        }

        /* Variant & General Modal Custom Items */
        .variant-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          margin-top: 14px;
        }

        .btn-variant-choice {
          padding: 12px;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #cbd5e1;
          cursor: pointer;
          text-align: center;
          font-weight: 700;
          font-size: 13px;
          color: #334155;
          transition: all 0.2s;
        }

        .btn-variant-choice:hover {
          border-color: #3b82f6;
          background: #f0f6ff;
          color: #2563eb;
        }

        .checkout-modal-columns {
          display: flex;
          gap: 24px;
          margin-top: 12px;
        }

        .checkout-left-col {
          flex: 1.2;
          border-right: 1px solid #e2e8f0;
          padding-right: 24px;
        }

        .checkout-right-col {
          flex: 0.8;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .pay-method-tabs {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
          margin-bottom: 16px;
        }

        .pay-tab {
          padding: 10px;
          border-radius: 8px;
          border: 1px solid #cbd5e1;
          background: white;
          font-size: 12px;
          font-weight: 700;
          color: #475569;
          cursor: pointer;
          text-align: center;
          transition: all 0.2s;
        }

        .pay-tab.active {
          background: #10b981;
          color: white;
          border-color: #10b981;
        }

        .quick-cash-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 6px;
          margin-top: 8px;
        }

        .btn-cash-helper {
          padding: 8px;
          border-radius: 6px;
          background: #f1f5f9;
          border: 1px solid #cbd5e1;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          text-align: center;
        }
        .btn-cash-helper:hover { background: #e2e8f0; }

        .payment-list-wrapper {
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          max-height: 110px;
          overflow-y: auto;
          padding: 8px;
          margin-bottom: 12px;
          background: #f8fafc;
        }

        .payment-list-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          font-weight: 700;
          padding: 4px 6px;
          border-bottom: 1px solid #e2e8f0;
        }

        /* Success screen ticket simulator */
        .ticket-simulator {
          background: #fdfdfd;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          padding: 16px;
          font-family: 'Courier New', Courier, monospace;
          font-size: 12px;
          line-height: 1.4;
          max-height: 250px;
          overflow-y: auto;
          margin: 14px 0;
          box-shadow: inset 0 2px 8px rgba(0,0,0,0.05);
        }
      `}</style>

      {/* Top Header Bar */}
      <header className="pos-header">
        <div className="header-left">
          <button onClick={() => navigate('/salon')} className="btn-back" title="Volver al salón">
            <FaArrowLeft />
          </button>
          <div className="table-details">
            <h1 className="table-title">{table?.name || `Mesa #${table?.number}`}</h1>
            <div className="table-meta">
              <span className="table-sector">Sector: {table?.sector}</span>
              <span>·</span>
              <span className={`badge-status ${table?.status}`}>{table?.status === 'waiting_payment' ? 'Esperando pago' : 'Ocupada'}</span>
            </div>
          </div>
        </div>

        {/* Center / Right header info */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#475569' }}>
            <FaUser />
            <span>Mozo: {order?.waiterId?.name || 'Cargando...'}</span>
          </div>
          <button 
            onClick={() => saveOrderItems(true)} 
            disabled={isSaving}
            className="btn-action btn-nav"
            style={{ padding: '8px 14px', fontSize: 12 }}
          >
            {isSaving ? '⏳ Guardando...' : '💾 Guardar Mesa'}
          </button>
        </div>
      </header>

      {/* Split screen content layout */}
      <main className="pos-main">
        {/* Left Panel: Active bill detail */}
        <section className="order-panel">
          {/* Diners Count Selector */}
          <div className="order-diners-bar">
            <span style={{ fontSize: 13, fontWeight: 700, color: '#475569', display: 'flex', alignItems: 'center', gap: 6 }}>
              <FaUsers /> Comensales
            </span>
            <div className="diners-selector">
              <button onClick={() => setDinersCount(prev => Math.max(1, prev - 1))} className="diners-btn">-</button>
              <span style={{ fontSize: 14, fontWeight: 800, width: 20, textAlign: 'center' }}>{dinersCount}</span>
              <button onClick={() => setDinersCount(prev => prev + 1)} className="diners-btn">+</button>
            </div>
          </div>

          {/* Ordered consumption list */}
          <div className="order-items-list">
            {localItems.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>
                🍽️ Mesa vacía. Agregá productos del menú de la derecha.
              </div>
            ) : (
              localItems.map((item, idx) => (
                <div 
                  key={idx} 
                  className={`order-item-card ${item.sentToKitchen ? 'kitchen' : 'draft'}`}
                >
                  <div className="item-info">
                    <span className="item-name">{item.name}</span>
                    <span className="item-price">${Number(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                  {item.variantName && (
                    <div className="item-variant">{item.variantName}</div>
                  )}
                  {item.notes && (
                    <div className="item-notes">{item.notes}</div>
                  )}

                  <div className="item-controls-row">
                    <div className="qty-controls">
                      <button onClick={() => adjustQuantity(idx, -1)} className="qty-btn"><FaMinus /></button>
                      <span className="qty-value">{item.quantity}</span>
                      <button onClick={() => adjustQuantity(idx, 1)} className="qty-btn"><FaPlus /></button>
                    </div>

                    <div className="item-action-btns">
                      <button 
                        onClick={() => openNoteForItem(idx)} 
                        className="item-action-btn" 
                        title="Agregar nota"
                      >
                        <FaRegNoteSticky />
                      </button>
                      <button 
                        onClick={() => adjustQuantity(idx, -item.quantity)} 
                        className="item-action-btn delete" 
                        title="Remover producto"
                      >
                        <FaTrash />
                      </button>
                      <span className={`badge-kitchen-status ${item.sentToKitchen ? 'sent' : 'draft'}`}>
                        {item.sentToKitchen ? <FaCheckDouble title="Enviado a cocina" /> : <FaCheck title="Borrador" />}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Quick Notes Input */}
          <div style={{ padding: '0 16px', background: '#f8fafc' }}>
            <input 
              type="text" 
              placeholder="✍️ Notas generales de la mesa (Ej: Sin cebolla)..." 
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              style={{ width: '100%', padding: 8, fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 8, outline: 'none' }}
            />
          </div>

          {/* Totals Summary */}
          <div className="order-summary-box">
            <div className="summary-row">
              <span>Subtotal consumido</span>
              <span>${localSubtotal.toFixed(2)}</span>
            </div>
            <div className="summary-row total">
              <span>TOTAL</span>
              <span>${localSubtotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Action Button Grid */}
          <div className="order-actions-grid">
            <button onClick={handleSendToKitchen} className="btn-pos-action comanda">
              <FaKitchenSet /> Comanda
            </button>
            <button onClick={handleRequestPreBill} className="btn-pos-action precuenta">
              <FaFileInvoiceDollar /> Pre-cuenta
            </button>
            <button onClick={() => setShowTransferModal(true)} className="btn-pos-action mudar">
              <FaRightLeft /> Mudar Mesa
            </button>
            <button onClick={handleOpenCheckout} className="btn-pos-action cobrar">
              <FaCashRegister /> Cobrar Mesa
            </button>
          </div>
        </section>

        {/* Right Panel: Category scroll & product grid catalog */}
        <section className="catalog-panel">
          {/* Quick Search */}
          <div className="catalog-search-bar">
            <FaMagnifyingGlass className="search-icon" />
            <input 
              type="search" 
              placeholder="🔍 Buscar producto en el menú..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-search"
            />
          </div>

          {/* Categories Pill Horizontal Selector */}
          <div className="catalog-categories">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`cat-pill ${activeCategory === cat ? 'active' : ''}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Product card lists */}
          <div className="catalog-products-container">
            {filteredProducts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#94a3b8', fontSize: '14px', fontWeight: 600 }}>
                ❌ No se encontraron productos cargados
              </div>
            ) : (
              <div className="products-grid">
                {filteredProducts.map((prod) => (
                  <div
                    key={prod._id}
                    onClick={() => handleProductClick(prod)}
                    className="product-tactile-card"
                  >
                    <div className="prod-title">{prod.name}</div>
                    <span className="prod-price-badge">${Number(prod.salePrice || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      {/* MODAL: Mudar Mesa / Transfer (Table swap) */}
      {showTransferModal && (
        <div className="modal-overlay" onClick={() => setShowTransferModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">🔀 Mudar Mesa {table?.name || table?.number}</h2>
              <button onClick={() => setShowTransferModal(false)} className="btn-close"><FaXmark /></button>
            </div>
            <div className="form-group">
              <label className="form-label">Selecciona Mesa Libre Destino</label>
              {freeTables.length === 0 ? (
                <p style={{ color: '#ef4444', fontSize: 13, fontWeight: 700, padding: 8 }}>❌ No hay mesas libres en el salón actualmente</p>
              ) : (
                <select
                  value={selectedTargetTable}
                  onChange={(e) => setSelectedTargetTable(e.target.value)}
                  className="form-input"
                  required
                >
                  <option value="" disabled>Seleccionar mesa libre...</option>
                  {freeTables.map(t => (
                    <option key={t._id} value={t._id}>#{t.number} - {t.name || `Mesa ${t.number}`} (Sector: {t.sector || 'Salón Principal'})</option>
                  ))}
                </select>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" onClick={() => setShowTransferModal(false)} className="btn-action btn-muted">
                Cancelar
              </button>
              <button 
                type="button" 
                onClick={handleTransferTable} 
                disabled={!selectedTargetTable}
                className="btn-action btn-primary"
              >
                Transferir Cuenta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Nota de Item (Preparaciones especiales) */}
      {showNoteModal && (
        <div className="modal-overlay" onClick={() => setShowNoteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">✍️ Instrucción de Preparación</h2>
              <button onClick={() => setShowNoteModal(false)} className="btn-close"><FaXmark /></button>
            </div>
            <div className="form-group">
              <label className="form-label">Notas del plato (Ej: Sin sal, Hielo separado, etc.)</label>
              <textarea
                value={tempNoteText}
                onChange={(e) => setTempNoteText(e.target.value)}
                className="form-input"
                style={{ height: 100, resize: 'none' }}
                placeholder="Ej: Sin cebolla, extra aderezo..."
              />
            </div>
            <div className="modal-footer">
              <button type="button" onClick={() => setShowNoteModal(false)} className="btn-action btn-muted">
                Cancelar
              </button>
              <button type="button" onClick={saveItemNote} className="btn-action btn-primary">
                Confirmar Nota
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Selección de Variantes (Drawer simulator popup) */}
      {showVariantModal && (
        <div className="modal-overlay" onClick={() => setShowVariantModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">🍕 Opciones de {variantProduct?.name}</h2>
              <button onClick={() => setShowVariantModal(false)} className="btn-close"><FaXmark /></button>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', fontWeight: 600 }}>Selecciona la variante que solicita el cliente:</p>
            <div className="variant-grid">
              {variantProduct?.variants.map((v, index) => {
                const desc = Object.entries(v.attributes || {}).map(([, val]) => `${val}`).join(' · ');
                return (
                  <button
                    key={v.sku || index}
                    onClick={() => {
                      addProductToOrder(variantProduct, v);
                      setShowVariantModal(false);
                    }}
                    className="btn-variant-choice"
                  >
                    <div>{desc || `Opción ${index + 1}`}</div>
                    <div style={{ color: '#10b981', marginTop: 4 }}>${Number(v.price || 0).toFixed(2)}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Cobro y Cierre integrado (Checkout) */}
      {showCheckoutModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 760, width: '95%' }}>
            
            {/* SUCCESS SCREEN STATE INSIDE CHECKOUT */}
            {showSuccessScreen ? (
              <div style={{ textAlign: 'center', padding: '16px 0' }}>
                <span style={{ fontSize: 60, display: 'block', marginBottom: 12 }}>🎉</span>
                <h2 style={{ margin: 0, fontWeight: 900, color: '#10b981' }}>Mesa Cerrada Exitosamente</h2>
                <p style={{ fontSize: 14, color: '#475569', marginTop: 8 }}>Los consumos han sido asentados en caja y el stock actualizado.</p>
                
                {/* simulated print receipt info */}
                <div className="ticket-simulator">
                  <div>========================================</div>
                  <div style={{ fontWeight: 800, textAlign: 'center' }}>VENTIO GASTRONOMIA</div>
                  <div style={{ textAlign: 'center' }}>Mesa #{table?.number} - {table?.sector}</div>
                  <div style={{ textAlign: 'center' }}>Venta #{successSale?.saleNumber || 'Temporal'}</div>
                  <div style={{ textAlign: 'center' }}>Fecha: {new Date().toLocaleString()}</div>
                  <div>========================================</div>
                  {localItems.map((it, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{it.quantity}x {it.name.substring(0, 15)}</span>
                      <span>${Number(it.price * it.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div>----------------------------------------</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                    <span>TOTAL FACTURADO:</span>
                    <span>${checkoutTotal.toFixed(2)}</span>
                  </div>
                  <div>========================================</div>
                  <div style={{ textAlign: 'center', fontSize: 10 }}>¡GRACIAS POR SU VISITA!</div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 24 }}>
                  {printUrl && (
                    <a 
                      href={printUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn-action btn-primary"
                      style={{ textDecoration: 'none' }}
                    >
                      <FaPrint /> Imprimir Comprobante
                    </a>
                  )}
                  <button onClick={finishPOSSession} className="btn-action btn-muted" style={{ padding: '12px 24px' }}>
                    Terminar y Volver
                  </button>
                </div>
              </div>
            ) : (
              // STANDARD CHECKOUT BILL SCREEN
              <>
                <div className="modal-header">
                  <h2 className="modal-title">💸 Cobro y Cierre: Mesa {table?.name || table?.number}</h2>
                  <button onClick={() => setShowCheckoutModal(false)} className="btn-close"><FaXmark /></button>
                </div>
                <form onSubmit={handleCheckoutSubmit}>
                  
                  <div className="checkout-modal-columns">
                    {/* Left Column Fields */}
                    <div className="checkout-left-col">
                      <div className="form-group">
                        <label className="form-label">Cliente (Cuenta Corriente / Factura)</label>
                        <select
                          value={checkoutCustomer}
                          onChange={(e) => setCheckoutCustomer(e.target.value)}
                          className="form-input"
                        >
                          <option value="">Consumidor Final</option>
                          {customers.map(c => (
                            <option key={c._id} value={c._id}>{c.nombre} {c.cuentaCorrienteEnabled ? '(CC Habilitada)' : ''}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Descuento de Mesa ($)</label>
                        <input
                          type="number"
                          min="0"
                          max={localSubtotal}
                          value={checkoutDiscount}
                          onChange={(e) => setCheckoutDiscount(Number(e.target.value))}
                          className="form-input"
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Método de Pago</label>
                        <div className="pay-method-tabs">
                          <button type="button" onClick={() => setPaymentType('Efectivo')} className={`pay-tab ${paymentType === 'Efectivo' ? 'active' : ''}`}>Efectivo</button>
                          <button type="button" onClick={() => setPaymentType('Tarjeta')} className={`pay-tab ${paymentType === 'Tarjeta' ? 'active' : ''}`}>Tarjeta</button>
                          <button type="button" onClick={() => setPaymentType('Transferencia')} className={`pay-tab ${paymentType === 'Transferencia' ? 'active' : ''}`}>Transf.</button>
                          {checkoutCustomer && customers.find(c => c._id === checkoutCustomer)?.cuentaCorrienteEnabled && (
                            <button type="button" onClick={() => setPaymentType('Cuenta corriente')} className={`pay-tab ${paymentType === 'Cuenta corriente' ? 'active' : ''}`}>Cuenta C.</button>
                          )}
                          <button type="button" onClick={() => { setPaymentType('Múltiple'); setPayments([]); }} className={`pay-tab ${paymentType === 'Múltiple' ? 'active' : ''}`}>Múltiple</button>
                        </div>
                      </div>

                      {/* Cash Helper inputs */}
                      {paymentType === 'Efectivo' && (
                        <div className="form-group">
                          <label className="form-label">Efectivo Recibido ($)</label>
                          <input
                            type="number"
                            value={cashReceived}
                            onChange={(e) => setCashReceived(e.target.value)}
                            placeholder={`Total: $${checkoutTotal.toFixed(2)}`}
                            className="form-input"
                          />
                          <div className="quick-cash-grid">
                            <button type="button" onClick={() => setCashReceived(checkoutTotal)} className="btn-cash-helper">Exacto</button>
                            <button type="button" onClick={() => setCashReceived(Math.ceil(checkoutTotal / 1000) * 1000)} className="btn-cash-helper">${Math.ceil(checkoutTotal / 1000) * 1000}</button>
                            <button type="button" onClick={() => setCashReceived(prev => (Number(prev) || 0) + 1000)} className="btn-cash-helper">+ $1000</button>
                            <button type="button" onClick={() => setCashReceived(prev => (Number(prev) || 0) + 5000)} className="btn-cash-helper">+ $5000</button>
                          </div>
                        </div>
                      )}

                      {/* Mixed multiple payment inputs */}
                      {paymentType === 'Múltiple' && (
                        <div style={{ border: '1px solid #cbd5e1', padding: 12, borderRadius: 10, background: '#f8fafc' }}>
                          <label className="form-label" style={{ marginBottom: 4 }}>Dividir Pago</label>
                          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                            <select value={tempPaymentMethod} onChange={(e) => setTempPaymentMethod(e.target.value)} className="form-input" style={{ flex: 1.2 }}>
                              <option value="Efectivo">Efectivo</option>
                              <option value="Tarjeta">Tarjeta</option>
                              <option value="Transferencia">Transferencia</option>
                              {checkoutCustomer && customers.find(c => c._id === checkoutCustomer)?.cuentaCorrienteEnabled && (
                                <option value="Cuenta corriente">Cuenta corriente</option>
                              )}
                            </select>
                            <input
                              type="number"
                              placeholder="Monto"
                              value={tempPaymentAmount}
                              onChange={(e) => setTempPaymentAmount(e.target.value)}
                              className="form-input"
                              style={{ flex: 1 }}
                            />
                            <button type="button" onClick={handleAddPayment} className="btn-action btn-primary" style={{ padding: '8px 12px' }}>Agregar</button>
                          </div>

                          <div className="payment-list-wrapper">
                            {payments.length === 0 ? (
                              <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', padding: '6px 0' }}>Agrega pagos para cubrir el total.</div>
                            ) : (
                              payments.map((p, i) => (
                                <div key={i} className="payment-list-item">
                                  <span>{p.method}</span>
                                  <span>${p.amount} <FaTrash onClick={() => handleRemovePayment(i)} style={{ color: '#ef4444', marginLeft: 8, cursor: 'pointer' }} /></span>
                                </div>
                              ))
                            )}
                          </div>

                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700 }}>
                            <span style={{ color: '#64748b' }}>Pagado: ${accumulatedMixedPaid.toFixed(2)}</span>
                            <span style={{ color: Math.abs(accumulatedMixedPaid - checkoutTotal) < 0.05 ? '#10b981' : '#ef4444' }}>Falta: ${(checkoutTotal - accumulatedMixedPaid).toFixed(2)}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column Details */}
                    <div className="checkout-right-col">
                      <div style={{ background: '#f1f5f9', padding: 18, borderRadius: 16 }}>
                        <h4 style={{ margin: '0 0 12px 0', fontSize: 14, fontWeight: 800, textTransform: 'uppercase', color: '#475569' }}>Resumen de Cuenta</h4>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8, fontWeight: 600, color: '#64748b' }}>
                          <span>Consumo Mesa</span>
                          <span>${localSubtotal.toFixed(2)}</span>
                        </div>
                        {checkoutDiscount > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8, fontWeight: 600, color: '#ef4444' }}>
                            <span>Descuento Aplicado</span>
                            <span>- ${checkoutDiscount.toFixed(2)}</span>
                          </div>
                        )}
                        <hr style={{ border: 'none', borderTop: '1px solid #cbd5e1', margin: '10px 0' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 900, color: '#0f172a' }}>
                          <span>A COBRAR</span>
                          <span>${checkoutTotal.toFixed(2)}</span>
                        </div>

                        {paymentType === 'Efectivo' && Number(cashReceived) > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 800, color: '#10b981', marginTop: 12 }}>
                            <span>VUELTO</span>
                            <span>${cashChange.toFixed(2)}</span>
                          </div>
                        )}
                      </div>

                      <div className="modal-footer">
                        <button type="button" onClick={() => setShowCheckoutModal(false)} className="btn-action btn-muted">
                          Atrás
                        </button>
                        <button 
                          type="submit" 
                          className="btn-action btn-primary"
                          style={{ background: '#10b981' }}
                          disabled={paymentType === 'Múltiple' && Math.abs(accumulatedMixedPaid - checkoutTotal) > 0.05}
                        >
                          Confirmar Cobro
                        </button>
                      </div>
                    </div>
                  </div>

                </form>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
}
